import { useEffect, useMemo, useState } from 'react';
import en from './locales/en.json';
import fr from './locales/fr.json';
import './App.css';
import Logo from './components/Logo';

const translations = { fr, en };

const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return { theme: 'light', manualOverride: false };
  }

  const storedTheme = window.localStorage.getItem('glamify-theme');
  if (storedTheme === 'light' || storedTheme === 'dark') {
    return { theme: storedTheme, manualOverride: true };
  }

  return window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
    ? { theme: 'dark', manualOverride: false }
    : { theme: 'light', manualOverride: false };
};

const getInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return 'fr';
  }

  const stored = window.localStorage.getItem('glamify-language');
  return stored === 'fr' || stored === 'en' ? stored : 'fr';
};

function App() {
  const [{ theme, manualOverride }, setThemeState] = useState(getInitialTheme);
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (manualOverride) {
      document.documentElement.dataset.theme = theme;
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }, [theme, manualOverride]);

  useEffect(() => {
    if (manualOverride) {
      window.localStorage.setItem('glamify-theme', theme);
    } else {
      window.localStorage.removeItem('glamify-theme');
    }
  }, [theme, manualOverride]);

  useEffect(() => {
    window.localStorage.setItem('glamify-language', language);
  }, [language]);

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event) => {
      const prefersDark = event.matches;
      setThemeState((current) =>
        current.manualOverride
          ? current
          : { theme: prefersDark ? 'dark' : 'light', manualOverride: false }
      );
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleToggleTheme = () => {
    setThemeState((current) => ({
      theme: current.theme === 'dark' ? 'light' : 'dark',
      manualOverride: true,
    }));
  };

  const handleToggleLanguage = () => {
    setLanguage((current) => (current === 'fr' ? 'en' : 'fr'));
  };

  const copy = useMemo(() => translations[language], [language]);
  const themeToggleLabel =
    theme === 'dark' ? copy.toggleThemeToLight : copy.toggleThemeToDark;

  return (
    <div className="app">
      <div className="header-controls" aria-label="Display preferences">
        <button
          type="button"
          className="language-toggle"
          onClick={handleToggleLanguage}
          aria-label={copy.toggleLanguage}
        >
          {language === 'fr' ? 'FR' : 'EN'}
        </button>

        <button
          type="button"
          className="theme-toggle"
          onClick={handleToggleTheme}
          aria-label={themeToggleLabel}
        >
          {theme === 'dark' ? (
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
              <path
                d="M12 4.75a.75.75 0 0 1 .75-.75h.5a.75.75 0 0 1 0 1.5h-.5A.75.75 0 0 1 12 4.75Zm6.45 1.8a.75.75 0 0 1 1.06 0l.35.35a.75.75 0 1 1-1.06 1.06l-.35-.35a.75.75 0 0 1 0-1.06ZM12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9Zm0 7.5a3 3 0 1 1 0-6 3 3 0 0 1 0 6Zm9-.75a.75.75 0 0 1 0 1.5h-.5a.75.75 0 0 1 0-1.5h.5Zm-2.84 5.04a.75.75 0 0 1 0 1.06l-.35.35a.75.75 0 1 1-1.06-1.06l.35-.35a.75.75 0 0 1 1.06 0ZM12 18.25a.75.75 0 0 1 .75.75v.5a.75.75 0 0 1-1.5 0v-.5a.75.75 0 0 1 .75-.75Zm-7.25-.21.35.35a.75.75 0 0 1-1.06 1.06l-.35-.35a.75.75 0 0 1 1.06-1.06ZM4.75 12a.75.75 0 0 1-.75.75h-.5a.75.75 0 0 1 0-1.5h.5a.75.75 0 0 1 .75.75Zm1.8-6.45a.75.75 0 0 1-1.06 1.06l-.35-.35a.75.75 0 1 1 1.06-1.06l.35.35Z"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
              <path d="M21.53 15.88a.75.75 0 0 0-.83-.2 7.5 7.5 0 0 1-9.38-9.38.75.75 0 0 0-1.03-.91 9 9 0 1 0 11.32 11.32.75.75 0 0 0-.08-.83Z" />
            </svg>
          )}
        </button>
      </div>
      <div className="background-blob blob-one" aria-hidden="true" />
      <div className="background-blob blob-two" aria-hidden="true" />

      <main className="connection">
        <section className="connection-card">
          <header className="hero">
            <Logo theme={theme} />
            <p className="hero-subtitle">{copy.heroSubtitle}</p>
          </header>

          <h1>{copy.heading}</h1>

          <form
            className="signin-form"
            onSubmit={(event) => event.preventDefault()}
            noValidate
          >
            <label className="form-field">
              <span>{copy.emailLabel}</span>
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder={copy.emailPlaceholder}
                required
              />
            </label>

            <label className="form-field">
              <span>{copy.passwordLabel}</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                placeholder={copy.passwordPlaceholder}
                required
              />
            </label>

            <button type="submit" className="btn primary">
              {copy.submit}
            </button>
          </form>

          <div className="auth-divider">
            <span>{copy.divider}</span>
          </div>

          <div className="social-buttons">
            <button type="button" className="social-button google">
              <span className="social-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    fill="#4285F4"
                    d="M22 12.24c0-.74-.06-1.28-.19-1.84H12v3.34h5.68c-.11.83-.73 2.07-2.1 2.91l-.02.12 3.05 2.36.21.02C20.91 17.69 22 15.24 22 12.24"
                  />
                  <path
                    fill="#34A853"
                    d="M12 22c2.7 0 4.96-.88 6.61-2.38l-3.16-2.44c-.84.54-1.97.92-3.45.92-2.63 0-4.86-1.74-5.66-4.15l-.12.01-3.09 2.4-.04.11C3.93 19.45 7.64 22 12 22"
                  />
                  <path
                    fill="#FBBC05"
                    d="M6.34 13.95A5.97 5.97 0 0 1 5.97 12c0-.68.12-1.35.32-1.95l-.01-.13-3.13-2.43-.1.05A9.96 9.96 0 0 0 2 12c0 1.58.38 3.08 1.05 4.41z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.94c1.87 0 3.13.8 3.85 1.47l2.81-2.74C16.94 2.88 14.7 2 12 2 7.64 2 3.93 4.55 2.38 8.64l3.09 2.4c.8-2.41 3.03-4.15 5.66-4.15"
                  />
                </svg>
              </span>
              {copy.google}
            </button>
            <button type="button" className="social-button apple">
              <span className="social-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    fill="currentColor"
                    d="M17.53 12.82c-.02-2.04 1.67-3.02 1.75-3.07-0.96-1.4-2.45-1.6-2.96-1.61-1.26-.13-2.45.75-3.09.75-.64 0-1.62-.73-2.66-.71-1.36.02-2.63.79-3.33 2-1.42 2.46-.36 6.11 1.02 8.12.67.98 1.47 2.08 2.51 2.04 1.01-.04 1.38-.66 2.58-.66 1.2 0 1.53.66 2.58.64 1.07-.02 1.74-.99 2.39-1.98.76-1.11 1.07-2.18 1.08-2.23-.02-.01-2.06-.79-2.07-3.29z"
                  />
                  <path
                    fill="currentColor"
                    d="M15.51 6.78c.56-.68.94-1.62.84-2.55-.81.03-1.78.54-2.36 1.22-.52.6-.97 1.55-.85 2.46.9.07 1.81-.45 2.37-1.13z"
                  />
                </svg>
              </span>
              {copy.apple}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default App;
